import { config } from '~/app/config';
import type { Controller } from '~/server/src/interfaces';
import Turfmaster, { StateMachineValue } from '~/server/src/tmmodel';
import { getPreferredReconnectGameId } from '~/setup/reconnectStorage';

import { getAuthToken } from './authStorage';
import { gameLogger } from './logger';

// Dynamic import for SSR compatibility
/** Socket subset used by spectator saga (emit/once). */
interface SpectatorSocket {
  connected: boolean;
  emit: (event: string, payload?: unknown) => void;
  once: (event: string, listener: () => void) => void;
}

/** Minimal socket.io client surface for connectionparams + handler cleanup. */
type ConnectionParamsListener = () => void | Promise<void>;

interface SocketWithConnectionParams {
  id?: string;
  on: (event: string, listener: ConnectionParamsListener) => void;
  off?: (event: string, listener: ConnectionParamsListener) => void;
}

/** Params shape for RESTOREGAME / REJOINGAME receiveMsg callbacks (game slice only). */
interface RestoreRejoinMsgParams {
  game?: {
    currentState?: number;
  };
}

type PlayerViewWithReceiveMsg = {
  receiveMsg?: (state: StateMachineValue, params: unknown) => unknown;
};

/** Instance shape from the dynamically loaded server ControllerRemote (submodule). reconnectSocket is optional; server class may not implement it yet. */
interface ControllerRemoteInstance extends Controller {
  waitForConnection(): Promise<void>;
  reconnectSocket?(host: string, token: string): Promise<void>;
  userId: string | null;
  serverTime: string | null;
  socket?: SpectatorSocket;
  playerView?: { receiveMsg: (state: StateMachineValue, params: unknown) => Promise<void> };
}

interface ControllerRemoteType {
  new (host: string, token: string): ControllerRemoteInstance;
}

let ControllerRemote: ControllerRemoteType | undefined = undefined;
let controllerRemoteImport: Promise<ControllerRemoteType> | null = null;

async function getControllerRemoteClass(): Promise<ControllerRemoteType> {
  if (ControllerRemote) {
    return ControllerRemote;
  }

  if (typeof window === 'undefined') {
    throw new Error('ControllerRemote not available');
  }

  if (!controllerRemoteImport) {
    controllerRemoteImport = import('~/server/src/tmcontrollerremote')
      .then((mod) => {
        ControllerRemote = mod.default as ControllerRemoteType;
        return ControllerRemote;
      })
      .catch((error) => {
        controllerRemoteImport = null;
        throw error;
      });
  }

  return controllerRemoteImport;
}

interface ControllerRemoteWrapperOptions {
  host: string;
  onTokenExpired?: () => void;
  onReconnected?: () => void;
  /**
   * Controls whether this wrapper maintains a long-lived, auto-rejoining connection.
   * - `probe`: lightweight start/menu probing (RESTOREGAME only), no auto-rejoin, no token monitoring.
   * - `live`: in-game usage (auto-rejoin on socket reconnect, token monitoring enabled).
   */
  purpose?: 'probe' | 'live';
}

export class ControllerRemoteWrapper {
  private controller: InstanceType<ControllerRemoteType> | null = null;
  private host: string;
  private onTokenExpired?: () => void;
  private onReconnected?: () => void;
  private purpose: 'probe' | 'live';
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private tokenExpirationCheckInterval: NodeJS.Timeout | null = null;
  private hasSeenInitialConnect = false;
  private lastRejoinAttemptAtMs = 0;
  private rejoinAttemptInFlight = false;
  private connectHandler: ConnectionParamsListener | null = null;
  private connectHandlerSocketId: string | null = null;
  private attachedSocketRef: SocketWithConnectionParams | null = null;

  constructor(options: ControllerRemoteWrapperOptions) {
    if (!options.host) {
      throw new Error('ControllerRemoteWrapper: host is required');
    }
    this.host = options.host;
    this.onTokenExpired = options.onTokenExpired;
    this.onReconnected = options.onReconnected;
    this.purpose = options.purpose ?? 'live';
  }

  public getPurpose(): 'probe' | 'live' {
    return this.purpose;
  }

  public setPurpose(purpose: 'probe' | 'live'): void {
    this.purpose = purpose;
  }

  public connect = async (): Promise<void> => {
    try {
      gameLogger.info(
        { host: this.host, purpose: this.purpose },
        'ControllerRemoteWrapper: connect() called',
      );

      if (!this.host) {
        gameLogger.error({ host: this.host }, 'ControllerRemoteWrapper: host is null/undefined');
        throw new Error('ControllerRemoteWrapper: host is not set');
      }

      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token available');
      }

      const RemoteController = await getControllerRemoteClass();

      gameLogger.info(
        { host: this.host, purpose: this.purpose },
        'ControllerRemoteWrapper: Connecting',
      );
      this.controller = new RemoteController(this.host, token);
      await this.controller.waitForConnection();

      // After the first successful connection, attach a listener for subsequent
      // socket.io reconnects. Those reconnects can happen transparently and we must
      // explicitly ask the server to resume the live game state.
      this.hasSeenInitialConnect = true;
      if (this.purpose === 'live') {
        this.attachAutoRejoinOnSocketReconnect();
      }

      // Reset reconnect attempts on successful connection
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;

      gameLogger.info({ purpose: this.purpose }, 'ControllerWrapper: Connected successfully');
      if (this.purpose === 'live') {
        this.startTokenExpirationMonitoring();
      }
      // Historically this callback is used as "connected/reconnected" signal. Avoid firing it
      // for probe-only usage to prevent misleading "reconnected" logs on the start page.
      if (this.purpose === 'live') {
        this.onReconnected?.();
      }
    } catch (error) {
      gameLogger.error({ err: error }, 'ControllerWrapper: Connection failed');
      throw error;
    }
  };

  public reconnect = async (): Promise<void> => {
    if (this.purpose !== 'live') {
      gameLogger.debug(
        { purpose: this.purpose },
        'ControllerWrapper: reconnect() ignored for non-live wrapper',
      );
      return;
    }
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      gameLogger.error('ControllerWrapper: Max reconnection attempts reached');
      throw new Error('Max reconnection attempts reached');
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff

    gameLogger.info(
      {
        purpose: this.purpose,
        attempt: this.reconnectAttempts,
        max: this.maxReconnectAttempts,
        delayMs: delay,
      },
      'ControllerWrapper: Attempting reconnection',
    );

    await new Promise((resolve) => setTimeout(resolve, delay));

    const token = getAuthToken();
    if (!token) {
      throw new Error('No auth token available for reconnect');
    }

    // Reuse same controller so the saga keeps a valid reference (no stale controller after idle/token refresh)
    if (this.controller && typeof this.controller.reconnectSocket === 'function') {
      await this.controller.reconnectSocket(this.host, token);
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      // Reattach reconnect listener after socket re-init.
      this.attachAutoRejoinOnSocketReconnect();
      // If the reconnect already triggered `connectionparams`, our listener might not fire.
      // Trigger immediately to avoid missing the resync window.
      await this.connectHandler?.();
      this.onReconnected?.();
      return;
    }

    // No reconnectSocket: preserve view, close old controller, then create new one and reattach view
    const preservedView =
      this.controller &&
      typeof (this.controller as { playerView?: unknown }).playerView !== 'undefined'
        ? (
            this.controller as {
              playerView: {
                receiveMsg: (state: StateMachineValue, params: unknown) => Promise<void>;
              };
            }
          ).playerView
        : undefined;
    if (this.controller) {
      this.controller.closeConnection?.();
      this.controller = null;
    }
    await this.connect();
    if (preservedView && this.controller) {
      (
        this.controller as {
          playerView?: { receiveMsg: (state: StateMachineValue, params: unknown) => Promise<void> };
        }
      ).playerView = preservedView;
      gameLogger.debug('ControllerWrapper: Reattached playerView after reconnect');
    }
    // Same rationale as above: make sure we actively resync after reconnect.
    await this.connectHandler?.();
  };

  public handleTokenRefresh = async (): Promise<void> => {
    if (this.purpose !== 'live') {
      gameLogger.debug(
        { purpose: this.purpose },
        'ControllerWrapper: Ignoring token refresh for non-live wrapper',
      );
      return;
    }
    gameLogger.info({ purpose: this.purpose }, 'ControllerWrapper: Handling token refresh');

    // Wait a bit for the token to be refreshed by AuthContext
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Try to reconnect with the new token
    try {
      await this.reconnect();
    } catch (error) {
      gameLogger.error(
        { err: error },
        'ControllerWrapper: Failed to reconnect after token refresh',
      );
    }
  };

  public getController(): InstanceType<ControllerRemoteType> | null {
    return this.controller;
  }

  public disconnect = (): void => {
    this.stopTokenExpirationMonitoring();
    if (this.controller) {
      this.controller.closeConnection?.();
      this.controller = null;
    }
    this.connectHandler = null;
    this.connectHandlerSocketId = null;
    this.attachedSocketRef = null;
    this.hasSeenInitialConnect = false;
  };

  public isConnected = (): boolean => {
    return this.controller !== null && this.controller.socket?.connected === true;
  };

  public getHost(): string {
    return this.host;
  }

  private startTokenExpirationMonitoring(): void {
    // Check token expiration every minute
    this.tokenExpirationCheckInterval = setInterval(() => {
      const token = getAuthToken();
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const expirationTime = payload.exp * 1000; // Convert to milliseconds
          const currentTime = Date.now();
          const timeUntilExpiration = expirationTime - currentTime;

          // If token expires in less than 5 minutes, trigger refresh
          if (timeUntilExpiration < 5 * 60 * 1000) {
            gameLogger.info(
              { purpose: this.purpose },
              'ControllerWrapper: Token expires soon, triggering refresh',
            );
            this.onTokenExpired?.();
            this.handleTokenRefresh();
          }
        } catch (error) {
          gameLogger.warn({ err: error }, 'ControllerWrapper: Error parsing token');
        }
      }
    }, 60 * 1000); // Check every minute
  }

  private stopTokenExpirationMonitoring(): void {
    if (this.tokenExpirationCheckInterval) {
      clearInterval(this.tokenExpirationCheckInterval);
      this.tokenExpirationCheckInterval = null;
    }
  }

  /**
   * Auto-resume live games after a transparent socket.io reconnect.
   *
   * Socket reconnection alone does not guarantee that the server re-sends the
   * current controller state (NEWGAME/JOINGAME/STARTGAME/...). We request a
   * REJOINGAME to force the controller to refresh game + board state.
   */
  private attachAutoRejoinOnSocketReconnect(): void {
    if (!this.controller) return;
    if (this.purpose !== 'live') return;

    const socket = (this.controller as unknown as { socket?: SocketWithConnectionParams }).socket;
    if (!socket || typeof socket.on !== 'function') {
      return;
    }

    const socketId = typeof socket.id === 'string' ? socket.id : null;

    // If socket instance didn't change, avoid registering multiple handlers.
    if (
      this.connectHandler &&
      socketId &&
      socketId === this.connectHandlerSocketId &&
      this.attachedSocketRef === socket
    ) {
      return;
    }

    // Detach from previous socket instance (if any) to avoid losing the handler after reconnect.
    if (this.connectHandler && this.attachedSocketRef && this.attachedSocketRef !== socket) {
      const prevSocket = this.attachedSocketRef;
      if (typeof prevSocket.off === 'function' && this.connectHandler) {
        const handler = this.connectHandler;
        try {
          prevSocket.off('connectionparams', handler);
        } catch {
          // ignore
        }
      }
    }

    if (this.connectHandler && typeof socket.off === 'function' && this.connectHandlerSocketId) {
      try {
        const handler = this.connectHandler;
        socket.off('connectionparams', handler);
      } catch {
        // ignore; best-effort cleanup
      }
    }
    this.attachedSocketRef = socket;

    const handler = async () => {
      // Only resume after initial connect succeeded.
      if (!this.hasSeenInitialConnect) return;

      // During configuration/join routes, auto rejoin can race with join/newgame handshakes
      // and steal the controller view callback routing. Skip auto rejoin there.
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      const isConfigOrJoinRoute =
        pathname === '/freegames' ||
        pathname === '/freegames/create' ||
        pathname === '/tournaments' ||
        pathname === '/training/create' ||
        /^\/freegames\/[^/]+\/join$/.test(pathname) ||
        /^\/tournaments\/[^/]+\/join$/.test(pathname);
      if (isConfigOrJoinRoute) {
        gameLogger.debug(
          { pathname },
          'ControllerRemoteWrapper: auto rejoin skipped on config/join route',
        );
        return;
      }

      // Throttle aggressive reconnect churn.
      const now = Date.now();
      if (this.rejoinAttemptInFlight) return;
      if (now - this.lastRejoinAttemptAtMs < 1500) return;
      this.lastRejoinAttemptAtMs = now;
      this.rejoinAttemptInFlight = true;

      try {
        gameLogger.debug('ControllerRemoteWrapper: auto rejoin handler start');
        const controller = this.controller;
        if (!controller) return;

        // IMPORTANT: tmcontrollerremote.receiveMsg(...) assigns `this.playerView = params.view`.
        // We must not overwrite the existing view with `undefined`, so we wrap the existing
        // playerView.receiveMsg and keep forwarding all events.
        let originalView = (controller as ControllerRemoteInstance).playerView as
          | PlayerViewWithReceiveMsg
          | undefined;
        let originalReceiveMsg = originalView?.receiveMsg;
        if (!originalReceiveMsg || typeof originalReceiveMsg !== 'function') {
          // View can be assigned slightly after socket reconnection.
          const deadline = Date.now() + 2000;
          while (Date.now() < deadline) {
            originalView = (controller as ControllerRemoteInstance).playerView as
              | PlayerViewWithReceiveMsg
              | undefined;
            originalReceiveMsg = originalView?.receiveMsg;
            if (originalReceiveMsg && typeof originalReceiveMsg === 'function') break;
            await new Promise((r) => setTimeout(r, 100));
          }
        }
        if (!originalReceiveMsg || typeof originalReceiveMsg !== 'function') return;

        const withTimeout = async <T>(
          promise: Promise<T>,
          timeoutMs: number,
        ): Promise<T | null> => {
          const timeout = new Promise<null>((resolve) =>
            setTimeout(() => resolve(null), timeoutMs),
          );
          return Promise.race([promise, timeout]);
        };

        // Wait for RESTOREGAME response so we can trigger REJOINGAME only after the server confirms
        // the user has a restorable game (mirrors sagaRestoreGame).
        const preferredGameId = getPreferredReconnectGameId(controller.userId ?? undefined);
        const restoreOnce = async (): Promise<RestoreRejoinMsgParams | null> => {
          const restorePromise = new Promise<RestoreRejoinMsgParams | null>((resolve) => {
            const tempView = {
              receiveMsg: (state: StateMachineValue, params: unknown) => {
                // Forward to the existing event channel/view so Redux stays in sync.
                void originalReceiveMsg(state, params);

                if (state === Turfmaster.stateMachine.RESTOREGAME) {
                  const p = params as RestoreRejoinMsgParams;
                  gameLogger.debug(
                    { hasGame: !!p.game },
                    'ControllerRemoteWrapper: RESTOREGAME response received',
                  );
                  resolve(p);
                }
              },
            };

            // Kick off RESTOREGAME using the temporary forwarding view.
            gameLogger.debug('ControllerRemoteWrapper: sending RESTOREGAME');
            controller
              .receiveMsg(Turfmaster.stateMachine.RESTOREGAME, {
                view: tempView,
                gameId: preferredGameId,
              })
              .catch(() => {
                // ignore; restorePromise will timeout
              });
          });

          return withTimeout(restorePromise, 2000);
        };

        let restoreParams = await restoreOnce();
        if (!restoreParams?.game) {
          gameLogger.debug('ControllerRemoteWrapper: RESTOREGAME null game; retrying shortly');
          await new Promise((r) => setTimeout(r, 500));
          restoreParams = await restoreOnce();
        }
        if (!restoreParams?.game) {
          gameLogger.debug(
            'ControllerRemoteWrapper: RESTOREGAME returned null game; aborting REJOINGAME',
          );
          return;
        }
        const rejoinOnce = async (): Promise<RestoreRejoinMsgParams | null> => {
          const rejoinPromise = new Promise<RestoreRejoinMsgParams | null>((resolve) => {
            const tempViewForRejoin = {
              receiveMsg: (state: StateMachineValue, params: unknown) => {
                if (state === Turfmaster.stateMachine.REJOINGAME) {
                  const p = params as RestoreRejoinMsgParams;
                  gameLogger.debug(
                    { hasGame: !!p.game, currentState: p.game?.currentState },
                    'ControllerRemoteWrapper: REJOINGAME response received',
                  );
                  resolve(p);
                }
                void originalReceiveMsg(state, params);
              },
            };

            gameLogger.debug('ControllerRemoteWrapper: sending REJOINGAME');
            controller
              .receiveMsg(Turfmaster.stateMachine.REJOINGAME, {
                view: tempViewForRejoin,
                gameId: preferredGameId,
              })
              .catch(() => {
                // ignore; rejoinPromise will timeout
              });
          });

          return withTimeout(rejoinPromise, 4000);
        };

        // If REJOINGAME races or times out, retry once. Do **not** treat every
        // `currentState < STARTGAME` as "not ready": lobby is `NEWGAME`, and the
        // model may still carry transient join states (`JOINGAME`, `JOINGAMEOPP`, `REJOINGAME`)
        // whose numeric values are also < `STARTGAME` (e.g. JOINGAMEOPP = 4), which would
        // otherwise spuriously retry and spam reconnect work.
        const maxRejoinAttempts = 2;
        for (let attempt = 0; attempt < maxRejoinAttempts; attempt++) {
          const rejoinParams = await rejoinOnce();
          const currentState = rejoinParams?.game?.currentState;

          if (typeof currentState === 'number') {
            const started = currentState >= Turfmaster.stateMachine.STARTGAME;
            const lobbyOrJoinHandshake =
              currentState === Turfmaster.stateMachine.NEWGAME ||
              currentState === Turfmaster.stateMachine.JOINGAME ||
              currentState === Turfmaster.stateMachine.JOINGAMEOPP ||
              currentState === Turfmaster.stateMachine.REJOINGAME;
            if (started || lobbyOrJoinHandshake) return;
          }

          if (attempt < maxRejoinAttempts - 1) {
            gameLogger.debug(
              'ControllerRemoteWrapper: REJOINGAME not conclusive; retrying REJOINGAME once',
            );
            await new Promise((r) => setTimeout(r, 1000));
          }
        }
      } catch (err) {
        gameLogger.error({ err }, 'ControllerRemoteWrapper: auto rejoin failed');
      } finally {
        this.rejoinAttemptInFlight = false;
      }
    };

    this.connectHandler = handler;
    this.connectHandlerSocketId = socketId;
    // Wait for server-side handshake (userId/serverTime) before triggering re-sync.
    socket.on('connectionparams', handler);
  }
}

// Global instance for the app
let globalControllerRemoteWrapper: ControllerRemoteWrapper | null = null;

export const createControllerRemoteWrapper = (
  options: ControllerRemoteWrapperOptions,
): ControllerRemoteWrapper => {
  gameLogger.info({ host: options.host }, 'createControllerRemoteWrapper: Creating new wrapper');

  if (globalControllerRemoteWrapper) {
    gameLogger.debug('createControllerRemoteWrapper: Disconnecting existing wrapper');
    globalControllerRemoteWrapper.disconnect();
  }

  globalControllerRemoteWrapper = new ControllerRemoteWrapper(options);
  gameLogger.info(
    { host: globalControllerRemoteWrapper['host'] },
    'createControllerRemoteWrapper: Created wrapper',
  );
  return globalControllerRemoteWrapper;
};

export const getControllerRemoteWrapper = (): ControllerRemoteWrapper | null => {
  if (globalControllerRemoteWrapper) {
    gameLogger.debug(
      { host: globalControllerRemoteWrapper['host'] },
      'getControllerRemoteWrapper: Returning existing wrapper',
    );
  } else {
    gameLogger.debug('getControllerRemoteWrapper: No existing wrapper found');
  }
  return globalControllerRemoteWrapper;
};

export interface EnsureControllerWrapperOptions {
  onTokenExpired?: () => void;
  onReconnected?: () => void;
  purpose?: 'probe' | 'live';
}

/**
 * Get or create the controller wrapper and ensure it is connected (restore-style behavior).
 * Used by mainSaga (rejoin/restore, online game) and sagaSpectator when opening spectate without lobby.
 * If a wrapper exists but has no controller (e.g. previous mainSaga was cancelled mid-connect),
 * we connect again so the caller gets a valid controller.
 */
export async function ensureControllerWrapperConnected(
  options?: EnsureControllerWrapperOptions,
): Promise<ControllerRemoteWrapper | null> {
  let wrapper = getControllerRemoteWrapper();
  if (!wrapper && config.baseUrl) {
    try {
      wrapper = createControllerRemoteWrapper({
        host: config.baseUrl,
        onTokenExpired: options?.onTokenExpired,
        onReconnected: options?.onReconnected,
        purpose: options?.purpose ?? 'live',
      });
      await wrapper.connect();
      wrapper = getControllerRemoteWrapper();
    } catch {
      wrapper = null;
    }
  }
  if (wrapper && options?.purpose) {
    wrapper.setPurpose(options.purpose);
  }
  // Wrapper exists but getController() is null: e.g. takeLatest cancelled mainSaga(rejoinGame)
  // before connect() completed. Ensure we have a controller before returning.
  if (wrapper && wrapper.getController() === null && config.baseUrl) {
    try {
      await wrapper.connect();
    } catch {
      return null;
    }
  }
  return wrapper;
}

export const destroyControllerRemoteWrapper = (): void => {
  if (globalControllerRemoteWrapper) {
    globalControllerRemoteWrapper.disconnect();
    globalControllerRemoteWrapper = null;
  }
};

export default ControllerRemoteWrapper;
